import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import ContactForm from "../components/ContactForm";
import { Seo } from "../components/Seo";
import { company, services, stats, whoWeAre, testimonials, images } from "../mock";

const STAR_RATING = ["s1", "s2", "s3", "s4", "s5"];

const WHY_POINTS = [
  "Solutions architected around your business — never off-the-shelf templates",
  "Transparent, small-business-friendly pricing with zero surprise costs",
  "Engineers who live on the cutting edge — AI, cloud, automation and security",
  "Straight-talking advice and measurable results at every milestone",
];

const SectionHeading = ({ eyebrow, title, sub, center }) => (
  <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
    {eyebrow && (
      <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">
        {eyebrow}
      </span>
    )}
    <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 text-balance">
      {title}
    </h2>
    {sub && <p className="mt-4 text-slate-500 leading-relaxed">{sub}</p>}
  </div>
);

const StarRow = () => (
  <div className="flex items-center gap-1">
    {STAR_RATING.map((k) => <Star key={k} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
  </div>
);

const HeroSection = () => (
  <section className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
    <div className="absolute inset-0 grid-pattern opacity-70" />
    <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
    <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
    <div className="container-wide relative grid lg:grid-cols-2 gap-12 items-center">
      <div className="animate-fade-up">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-semibold text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> Your tech partner for ambitious businesses
        </span>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05] text-balance">
          IT Support, SEO Services &amp; Managed IT Services
        </h1>
        <p className="mt-5 text-lg text-slate-500 leading-relaxed max-w-lg">
          {company.subtitle}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg" className="rounded-lg bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25">
            <a href="#about">Explore What We Do <ArrowRight className="h-4 w-4 ml-2" /></a>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-lg border-slate-300">
            <a href="#contact">Book a Free Strategy Call</a>
          </Button>
        </div>
        <div className="mt-8 flex items-center gap-5 text-sm text-slate-500">
          <StarRow />
          <span>Rated 5.0 by our clients</span>
        </div>
      </div>

      <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
        <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-300/50 border border-white">
          <img src={images.heroTeam} alt="IT professionals collaborating" className="w-full h-[440px] object-cover" />
        </div>
        <div className="absolute -bottom-6 -left-6 rounded-2xl bg-white shadow-xl border border-slate-100 p-5 hidden sm:block">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icons.ShieldCheck className="h-6 w-6" />
            </span>
            <div>
              <p className="font-display font-bold text-slate-900 text-lg leading-none">24/7</p>
              <p className="text-xs text-slate-500 mt-1">Proactive monitoring</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const StatsSection = () => (
  <section className="border-y border-slate-100 bg-white">
    <div className="container-wide grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-100">
      {stats.map((s) => (
        <div key={s.label} className="py-10 text-center">
          <p className="font-display text-4xl font-extrabold text-slate-900">{s.value}</p>
          <p className="mt-1 text-sm text-slate-500">{s.label}</p>
        </div>
      ))}
    </div>
  </section>
);

const ServicesSection = () => (
  <section className="py-24">
    <div className="container-wide">
      <SectionHeading eyebrow="What We Do" title="SEO services, IT consulting & managed IT services" sub="AI-powered SEO, strategic IT consulting and always-on managed IT support — three capabilities, one partner, zero jargon." center />
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {services.map((svc) => {
          const Icon = Icons[svc.icon] || Icons.Circle;
          return (
            <Link
              key={svc.id}
              to={svc.path}
              className="group relative rounded-2xl bg-white p-8 border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all duration-300"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                <Icon className="h-7 w-7" />
              </span>
              <h3 className="mt-6 font-display text-xl font-bold text-slate-900">{svc.title}</h3>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">{svc.short}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

const WhoWeAreSection = () => (
  <section id="about" className="py-24 bg-slate-50">
    <div className="container-wide">
      <SectionHeading eyebrow="Who We Are" title="Your trusted IT support & technology partner for over a decade" center />
      <div className="mt-14 grid gap-8 md:grid-cols-3">
        {whoWeAre.map((item) => (
          <div key={item.title} className="rounded-2xl bg-white overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg transition-shadow">
            <div className="h-48 overflow-hidden">
              <img src={item.img} alt={item.title} loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-7">
              <h3 className="font-display text-lg font-bold text-slate-900">{item.title}</h3>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">{item.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyChooseSection = () => (
  <section className="py-24">
    <div className="container-wide grid lg:grid-cols-2 gap-14 items-center">
      <div className="relative rounded-3xl overflow-hidden shadow-xl">
        <img src={images.collaboration} alt="OzzieeTech engineers collaborating on an IT support project" loading="lazy" decoding="async" className="w-full h-[420px] object-cover" />
      </div>
      <div>
        <SectionHeading eyebrow="Why OzzieeTech" title="Why businesses choose us for IT support" />
        <ul className="mt-8 space-y-5">
          {WHY_POINTS.map((point) => (
            <li key={point} className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
              <span className="text-slate-600">{point}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-sm text-slate-500">
          Curious where your website stands today? Try our free{" "}
          <Link to="/seo/auto-engine" className="font-semibold text-primary hover:underline">AI-powered SEO audit</Link>{" "}
          or explore our <Link to="/managed-services" className="font-semibold text-primary hover:underline">managed IT services</Link>.
        </p>
      </div>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
    <div className="absolute -top-24 right-0 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
    <div className="container-wide relative">
      <div className="max-w-2xl mx-auto text-center">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">Testimonials</span>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-balance">What businesses say about our IT services</h2>
      </div>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {testimonials.map((t) => (
          <div key={t.name} className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-7">
            <div className="mb-4"><StarRow /></div>
            <p className="text-slate-200 leading-relaxed">“{t.quote}”</p>
            <div className="mt-6 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary font-display font-bold">
                {t.name.charAt(0)}
              </span>
              <div>
                <p className="font-semibold text-white text-sm">{t.name}</p>
                <p className="text-xs text-slate-400">{t.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const AboutServicesSection = () => (
  <section className="py-24 bg-white border-t border-slate-100" data-testid="seo-text-section">
    <div className="container-wide grid gap-12 lg:grid-cols-2">
      <div>
        <SectionHeading eyebrow="Your Technology Partner" title="IT support, SEO and managed services that grow your business" />
        <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
          <p>
            OzzieeTech is a full-service technology partner for small and medium businesses. We combine three
            disciplines under one roof — <Link to="/seo" className="font-semibold text-primary hover:underline">search engine optimisation</Link>,{" "}
            <Link to="/consulting" className="font-semibold text-primary hover:underline">IT consulting</Link> and{" "}
            <Link to="/managed-services" className="font-semibold text-primary hover:underline">managed IT services</Link> — so
            you get one accountable team instead of juggling multiple vendors.
          </p>
          <p>
            Our SEO services help your website rank higher on Google and turn searches into customers. Every engagement
            starts with a deep technical and on-page audit: we analyse your titles, meta descriptions, heading structure,
            content quality, keywords and internal linking, then deliver a prioritised optimisation plan. You can even try
            it yourself in seconds with our free <Link to="/seo/auto-engine" className="font-semibold text-primary hover:underline">AI-powered SEO Auto Engine</Link>,
            which crawls any URL and generates an instant audit with keyword opportunities and AI-optimised meta tags.
          </p>
        </div>
      </div>
      <div className="space-y-4 text-slate-600 leading-relaxed lg:pt-24">
        <p>
          Our IT consulting covers the decisions that shape your technology future: cloud migration, cybersecurity,
          network architecture, software selection and digital transformation strategy. We translate business goals into
          practical, affordable technology roadmaps — no jargon, no over-engineering.
        </p>
        <p>
          With managed IT services, we become your outsourced IT department. That means proactive 24/7 monitoring,
          patching and updates, data backup and recovery, endpoint security and a responsive helpdesk — all for a
          predictable monthly subscription that scales with your team.
        </p>
        <p>
          Whether you need better search rankings, expert technology advice or complete IT management, OzzieeTech
          delivers honest, transparent and results-focused service. <a href="#contact" className="font-semibold text-primary hover:underline">Book a free consultation</a> and
          find out what the right technology partner can do for your business.
        </p>
      </div>
    </div>
  </section>
);

const ContactSection = () => (
  <section id="contact" className="py-24 bg-slate-50">
    <div className="container-wide">
      <SectionHeading eyebrow="Contact Us" title="Get IT support — free consultation" sub="Tell us where you want to go — we'll map the technology that gets you there. Expect a reply within one business day." center />
      <div className="mt-14">
        <ContactForm source="Home" />
      </div>
    </div>
  </section>
);

const Home = () => (
  <div>
    <Seo
      title="OzzieeTech — IT Support, SEO Services & Managed IT Services"
      description="Full-service technology partner for growing businesses: AI-powered SEO services, expert IT consulting and 24/7 managed IT support with transparent pricing."
      path="/"
    />
    <HeroSection />
    <StatsSection />
    <ServicesSection />
    <WhoWeAreSection />
    <WhyChooseSection />
    <TestimonialsSection />
    <AboutServicesSection />
    <ContactSection />
  </div>
);

export default Home;
